import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import MaterialIcon from "./MaterialIcon";

interface Ask {
  title: string;
  message: ReactNode;
  /** The confirming button's words; "Delete" by default. */
  action?: string;
  /** Whether the action is destructive, which colours the button. */
  danger?: boolean;
}

/**
 * A styled stand-in for window.confirm: `confirm(ask)` resolves true when
 * the action is chosen. Render `dialog` somewhere in the component.
 */
export function useConfirm(): { confirm: (ask: Ask) => Promise<boolean>; dialog: ReactNode } {
  const [ask, setAsk] = useState<Ask | null>(null);
  const answer = useRef<(yes: boolean) => void>(() => {});
  const confirm = useCallback(
    (next: Ask) =>
      new Promise<boolean>((resolve) => {
        answer.current = resolve;
        setAsk(next);
      }),
    [],
  );
  const close = (yes: boolean) => {
    setAsk(null);
    answer.current(yes);
  };
  useEffect(() => {
    if (!ask) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ask]);
  // On the page's body, so a closed drawer or pop-up holding the component
  // can't hide a question someone is waiting on.
  const dialog = ask && createPortal(
    <div className="modal-overlay" onClick={() => close(false)}>
      <div className="modal confirm" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 id="confirm-title">{ask.title}</h2>
          <button type="button" className="btn icon-btn" onClick={() => close(false)} aria-label="Close">
            <MaterialIcon name="close" />
          </button>
        </div>
        <div className="modal-body">
          <p>{ask.message}</p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn" onClick={() => close(false)}>
            Cancel
          </button>
          <button type="button" className={`btn${ask.danger === false ? " primary" : " danger"}`} onClick={() => close(true)}>
            {ask.action ?? "Delete"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
  return { confirm, dialog };
}
